import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';
import { sendPhoneOtp, verifyPhoneOtp } from '@/lib/onboarding-api';

export default function OtpScreen() {
    const t = useT();
    const navigate = useNavigate();
    const phone = useOnboardingStore((s) => s.phone);
    const setPhoneVerified = useOnboardingStore((s) => s.setPhoneVerified);
    const setVerification = useOnboardingStore((s) => s.setVerification);

    const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
    const [timer, setTimer] = useState(60);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [devOtp, setDevOtp] = useState<string | null>(null);
    const refs = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        refs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (timer <= 0) return;
        const id = setTimeout(() => setTimer(timer - 1), 1000);
        return () => clearTimeout(id);
    }, [timer]);

    const handleChange = (i: number, v: string) => {
        const char = v.replace(/[^\d]/g, '').slice(0, 1);
        const next = [...digits];
        next[i] = char;
        setDigits(next);
        if (char && i < 5) refs.current[i + 1]?.focus();
    };

    const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !digits[i] && i > 0) {
            refs.current[i - 1]?.focus();
        }
    };

    const complete = digits.every((d) => d !== '');

    const handleVerify = async () => {
        try {
            setLoading(true);
            setErr(null);
            await verifyPhoneOtp(digits.join(''));
            setPhoneVerified(true);
            setVerification({ phone: 'ok' });
            navigate('/onboarding/data-form');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        try {
            setErr(null);
            const res = await sendPhoneOtp();
            setTimer(60);
            setDigits(['', '', '', '', '', '']);
            refs.current[0]?.focus();
            if (res.devOtp) setDevOtp(res.devOtp);
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        }
    };

    return (
        <MobileFrame
            progress={(3 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 3, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{t.otpTitle}</h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 4,
                    }}
                >
                    {t.otpSubtitle}
                </p>
                <p
                    style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: 'var(--ink-900)',
                        marginBottom: 28,
                    }}
                >
                    +62 {phone || '812 •••• ••••'}
                </p>

                {devOtp && (
                    <div
                        className="chip chip-neutral"
                        style={{ marginBottom: 12, alignSelf: 'flex-start' }}
                    >
                        <Icon name="info" size={12} /> {t.otpDevHint} {devOtp}
                    </div>
                )}

                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        marginBottom: 20,
                        justifyContent: 'center',
                    }}
                >
                    {digits.map((d, i) => (
                        <input
                            key={i}
                            ref={(el) => {
                                refs.current[i] = el;
                            }}
                            value={d}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKey(i, e)}
                            maxLength={1}
                            inputMode="numeric"
                            style={{
                                width: 48,
                                height: 56,
                                borderRadius: 10,
                                border: `1.5px solid ${d ? 'var(--primary)' : 'var(--ink-200)'}`,
                                background: 'var(--surface)',
                                textAlign: 'center',
                                fontSize: 22,
                                fontWeight: 600,
                                color: 'var(--ink-900)',
                                outline: 'none',
                            }}
                        />
                    ))}
                </div>

                <div
                    style={{
                        textAlign: 'center',
                        fontSize: 13,
                        color: 'var(--ink-500)',
                        marginBottom: 14,
                    }}
                >
                    {timer > 0 ? (
                        <>
                            {t.otpResendIn}{' '}
                            <b style={{ color: 'var(--ink-900)' }}>
                                00:{timer.toString().padStart(2, '0')}
                            </b>
                        </>
                    ) : (
                        <button
                            onClick={handleResend}
                            style={{ color: 'var(--primary)', fontWeight: 600 }}
                        >
                            {t.otpResend}
                        </button>
                    )}
                </div>

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginBottom: 12, alignSelf: 'center' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ flex: 1 }} />

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={!complete || loading}
                    onClick={handleVerify}
                >
                    {loading ? <div className="spinner" /> : t.verify}
                </button>
            </div>
        </MobileFrame>
    );
}
